import api from './api';

// Types
export interface Author {
    name: string;
}

export interface Publisher {
    name: string;
    shortName?: string;
}

export interface SourceType {
    id: string;
    source_type: string;
    compulsory: string[];
    suggested: string[];
}

export interface MetadataField {
    id: string;
    source_type: string;
    field_name: string;
    field_type: 'text' | 'number' | 'date' | 'enum' | 'select';
    is_default: boolean;
    order_index: number;
    enum_options: string[] | null;
}

// Author Management APIs (via metadata_types)
export const getAllAuthors = async (): Promise<string[]> => {
    try {
        const response = await api.get('/admin/metadata-type/author');
        const enumOptions = response.data.enum_options || [];
        return enumOptions;
    } catch (error) {
        console.error('Error fetching authors:', error);
        throw error;
    }
};

export const addAuthor = async (name: string): Promise<void> => {
    try {
        const response = await api.get('/admin/metadata-type/author');
        const currentOptions = response.data.enum_options || [];
        if (!currentOptions.includes(name)) {
            await api.put('/admin/metadata-type/author/enum-options', {
                enum_options: [...currentOptions, name]
            });
        }
    } catch (error) {
        console.error('Error adding author:', error);
        throw error;
    }
};

export const removeAuthor = async (name: string): Promise<void> => {
    try {
        const response = await api.get('/admin/metadata-type/author');
        const currentOptions = response.data.enum_options || [];
        await api.put('/admin/metadata-type/author/enum-options', {
            enum_options: currentOptions.filter((n: string) => n !== name)
        });
    } catch (error) {
        console.error('Error removing author:', error);
        throw error;
    }
};

// Publisher Management APIs (via metadata_types)
export const getAllPublishers = async (): Promise<Publisher[]> => {
    try {
        const response = await api.get('/admin/metadata-type/publisher');
        const enumOptions = response.data.enum_options || [];
        // Parse publishers (format: "Name (ShortName)" or "Name")
        return enumOptions.map((option: string) => {
            const match = option.match(/^(.+?)\s*\((.+?)\)$/);
            if (match) {
                return { name: match[1], shortName: match[2] };
            }
            return { name: option };
        });
    } catch (error) {
        console.error('Error fetching publishers:', error);
        throw error;
    }
};

export const addPublisher = async (name: string, shortName?: string): Promise<void> => {
    try {
        const response = await api.get('/admin/metadata-type/publisher');
        const currentOptions = response.data.enum_options || [];
        const formattedName = shortName ? `${name} (${shortName})` : name;
        if (!currentOptions.includes(formattedName)) {
            await api.put('/admin/metadata-type/publisher/enum-options', {
                enum_options: [...currentOptions, formattedName]
            });
        }
    } catch (error) {
        console.error('Error adding publisher:', error);
        throw error;
    }
};

export const removePublisher = async (name: string): Promise<void> => {
    try {
        const response = await api.get('/admin/metadata-type/publisher');
        const currentOptions = response.data.enum_options || [];
        // Remove both formats: "Name (ShortName)" and just "Name"
        const filtered = currentOptions.filter((option: string) => {
            const match = option.match(/^(.+?)\s*\((.+?)\)$/);
            const optionName = match ? match[1] : option;
            return optionName !== name;
        });
        await api.put('/admin/metadata-type/publisher/enum-options', {
            enum_options: filtered
        });
    } catch (error) {
        console.error('Error removing publisher:', error);
        throw error;
    }
};

// Source Type Management APIs
export const getAllSourceTypes = async (): Promise<SourceType[]> => {
    try {
        const response = await api.get('/admin/source-types');
        return response.data;
    } catch (error) {
        console.error('Error fetching source types:', error);
        throw error;
    }
};

export const createSourceType = async (sourceType: SourceType['source_type'], compulsory: string[], suggested: string[]): Promise<SourceType> => {
    try {
        const response = await api.post('/admin/source-types', {
            source_type: sourceType,
            compulsory,
            suggested
        });
        return response.data;
    } catch (error) {
        console.error('Error creating source type:', error);
        throw error;
    }
};

export const updateSourceType = async (id: string, compulsory: string[], suggested: string[]): Promise<SourceType> => {
    try {
        const response = await api.put(`/admin/source-types/${id}`, {
            compulsory,
            suggested
        });
        return response.data;
    } catch (error) {
        console.error('Error updating source type:', error);
        throw error;
    }
};

export const deleteSourceType = async (id: string): Promise<void> => {
    try {
        await api.delete(`/admin/source-types/${id}`);
    } catch (error) {
        console.error('Error deleting source type:', error);
        throw error;
    }
};

// Metadata Management APIs
export interface MetadataType {
    id: string;
    field_name: string;
    field_type: 'text' | 'number' | 'date' | 'enum' | 'select';
    enum_options: (string | Record<string, any>)[] | null;
}

export const getAllMetadataTypes = async (): Promise<MetadataType[]> => {
    try {
        const response = await api.get('/admin/metadata-types');
        return response.data;
    } catch (error) {
        console.error('Error fetching metadata types:', error);
        throw error;
    }
};

export const createMetadataType = async (fieldName: string, fieldType: MetadataType['field_type'], enumOptions?: (string | Record<string, any>)[] | null): Promise<MetadataType> => {
    try {
        const response = await api.post('/admin/metadata-types', {
            field_name: fieldName,
            field_type: fieldType,
            enum_options: enumOptions || null
        });
        return response.data;
    } catch (error) {
        console.error('Error creating metadata type:', error);
        throw error;
    }
};

export const updateMetadataType = async (id: string, fieldName?: string, fieldType?: MetadataType['field_type'], enumOptions?: (string | Record<string, any>)[] | null): Promise<MetadataType> => {
    try {
        const response = await api.put(`/admin/metadata-types/${id}`, {
            ...(fieldName && { field_name: fieldName }),
            ...(fieldType && { field_type: fieldType }),
            ...(enumOptions !== undefined && { enum_options: enumOptions })
        });
        return response.data;
    } catch (error) {
        console.error('Error updating metadata type:', error);
        throw error;
    }
};

export const deleteMetadataType = async (id: string): Promise<void> => {
    try {
        await api.delete(`/admin/metadata-types/${id}`);
    } catch (error) {
        console.error('Error deleting metadata type:', error);
        throw error;
    }
};

export const getMetadataBySourceType = async (sourceType: string): Promise<MetadataField[]> => {
    try {
        const response = await api.get(`/admin/metadata/${sourceType}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching metadata:', error);
        throw error;
    }
};
